// SUB-OFFICE 7 — FORM 5-M, MANUAL CONVEYANCE DUTY. A parcel too fragile, too
// leaky, too forbidden, or too shameful for the tubes. Lift it at the origin
// cradle, carry it across the floor without jolting the seal or dripping the
// carpet or being seen (FORBIDDEN), and set it in the destination cradle before
// the deadline. Cradles sit AT authored waypoints (cradleCoord); the carry
// route samples at 100ms in waypoint space; the server recomputes cracks,
// drips, and forbidden observation from the raw route. Theater events are
// display-only.

import {
  CircleGeometry, Color, Group, Mesh, MeshBasicMaterial, Sprite, SpriteMaterial,
} from 'three';
import {
  cradleCoord, inCarpet5M, lampOn5M, LAMPS_5M, type Form5MSpec,
} from '../../shared/physspec';
import { PAL } from '../palette';
import { patchMaterial } from '../render/scene';
import { makeLabelTexture } from '../render/textures';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const WP2WORLD = (wx: number, wy: number): [number, number] => [31 - wx, 17 - wy];
const WORLD2WP = (x: number, z: number): [number, number] => [31 - x, 17 - z];

export function runConveyance(
  deps: BenchDeps,
  taskId: string,
  spec: Form5MSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM5M', taskId);
    const group = new Group();
    deps.sceneMgr.scene.add(group);

    const [owx, owy] = cradleCoord(spec.origin);
    const [dwx, dwy] = cradleCoord(spec.dest);
    const [ox, oz] = WP2WORLD(owx, owy);
    const [dx, dz] = WP2WORLD(dwx, dwy);
    const forbidden = spec.conditions.includes('FORBIDDEN');

    const mkDisc = (x: number, z: number, colorHex: number, top: string, sub: string): Mesh => {
      const mat = new MeshBasicMaterial({ color: new Color(colorHex).convertSRGBToLinear(), transparent: true, opacity: 0.8, depthWrite: false });
      patchMaterial(mat, { dimExempt: true });
      const disc = new Mesh(new CircleGeometry(0.6, 22), mat);
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(x, 0.03, z);
      group.add(disc);
      const label = new Sprite(new SpriteMaterial({ map: makeLabelTexture(top, sub), depthTest: false }));
      label.scale.set(1.8, 0.7, 1);
      label.position.set(x, 1.7, z);
      group.add(label);
      return disc;
    };
    mkDisc(ox, oz, PAL.amber, 'ORIGIN CRADLE', spec.origin);
    const destDisc = mkDisc(dx, dz, PAL.gas, 'DESTINATION', spec.dest);

    const condLine = spec.conditions.join(' + ');
    deps.bus.emit('chyron', `${condLine} PARCEL. ${spec.tag_flavor}.`);
    deps.bus.emit('context', `LIFT AT THE AMBER CRADLE. CARRY TO THE GREEN CRADLE WITHIN ${spec.deadline_s}s. DO NOT RUN.`);

    let carrying = false;
    let over = false;
    let raf = 0;
    let lastVX = 0;
    let lastVZ = 0;
    let lastX = deps.player.pos.x;
    let lastZ = deps.player.pos.z;
    let lastT = performance.now();
    let lastDrip = 0;
    const lampAccum = new Map<number, number>();

    const cleanup = (): void => {
      over = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      rec.stopRoute();
      deps.player.setCarry('none');
      deps.sceneMgr.scene.remove(group);
    };
    const abandon = (): void => {
      if (over) return;
      cleanup();
      deps.bus.emit('chyron', 'THE PARCEL RETURNS TO ITS CRADLE. THE DUTY REMAINS OPEN.');
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);

    const frame = (): void => {
      if (over) return;
      raf = requestAnimationFrame(frame);
      const now = performance.now();
      const dt = Math.min(0.1, (now - lastT) / 1000);
      lastT = now;
      const px = deps.player.pos.x;
      const pz = deps.player.pos.z;
      const vx = dt > 0 ? (px - lastX) / dt : 0;
      const vz = dt > 0 ? (pz - lastZ) / dt : 0;
      const speed = Math.hypot(vx, vz);
      lastX = px;
      lastZ = pz;

      if (!carrying) {
        if (Math.hypot(px - ox, pz - oz) <= 1.2) {
          carrying = true;
          rec.ev('picked_up', { origin: spec.origin });
          rec.startRoute(deps.player, 100, WORLD2WP);
          deps.player.setCarry('canister');
          deps.bus.emit('audio:tick');
          deps.bus.emit('context', forbidden
            ? 'CARRIED. STAY OUT OF THE LAMP SWEEPS. YOU ARE NOT TO BE SEEN WITH THIS.'
            : 'CARRIED. SMOOTH AND SLOW. SHARP TURNS CRACK THE SEAL; SPEED SPILLS IT.');
        }
      } else {
        // ---- theater feedback (display-only; server recomputes truth) ------
        // jolt on a sharp direction change at speed
        if (speed > 1.2) {
          const dot = (vx * lastVX + vz * lastVZ) / (speed * Math.hypot(lastVX, lastVZ) || 1);
          if (Math.hypot(lastVX, lastVZ) > 0.6 && dot < Math.cos((70 * Math.PI) / 180)) {
            rec.ev('jolt', { mag: Math.round((1 - dot) * 100) / 100 });
            deps.bus.emit('audio:buzz');
          }
        }
        // drip while fast (2.2s cadence; carpet doubles)
        const [wx, wy] = WORLD2WP(px, pz);
        const onCarpet = inCarpet5M(spec, wx, wy);
        const dripEvery = onCarpet ? 1100 : 2200;
        if (speed > 1.4 && rec.now() - lastDrip > dripEvery) {
          lastDrip = rec.now();
          rec.ev('drip', { cell: `${Math.round(wx)}:${Math.round(wy)}`, carpet: onCarpet ? 1 : 0 });
        }
        // FORBIDDEN: lamp observation
        if (forbidden) {
          LAMPS_5M.forEach((L, li) => {
            const inR = Math.hypot(wx - L.x, wy - L.y) <= L.r;
            const lit = lampOn5M(spec, li, rec.now());
            if (inR && lit) {
              lampAccum.set(li, (lampAccum.get(li) || 0) + dt * 1000);
            } else if (lampAccum.has(li)) {
              rec.ev('lamp_observed', { lamp_id: li, dur_ms: Math.round(lampAccum.get(li)!) });
              lampAccum.delete(li);
            }
          });
        }
        // delivery
        if (Math.hypot(px - dx, pz - dz) <= 1.2) {
          for (const [li, ms] of lampAccum) rec.ev('lamp_observed', { lamp_id: li, dur_ms: Math.round(ms) });
          rec.ev('delivered', { dest: spec.dest });
          (destDisc.material as MeshBasicMaterial).opacity = 1;
          rec.stopRoute();
          deps.player.setCarry('none');
          deps.bus.emit('audio:thunk');
          deps.bus.emit('chyron', 'THE PARCEL IS SET DOWN. THE CRADLE ACCEPTS IT. THE BACK OFFICE WILL WEIGH THE JOURNEY.');
          cleanup();
          const built = rec.build(true);
          if (built.valid) resolve(built.log);
          else {
            deps.bus.emit('toast', `The conveyance log did not assemble (${built.reason}).`);
            resolve(null);
          }
          return;
        }
      }
      lastVX = vx;
      lastVZ = vz;
    };
    raf = requestAnimationFrame(frame);
  });
}
